import { useEffect, useState, useMemo, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getTestDetail, createTest, updateTest } from '../api/testApi'
import { listTestGroups } from '../../testgroups/api/testGroupApi'
import { getMasterOptionItems } from '../../../admission/students/api/mastersApi'
import { appToast } from '../../../../components/AppToast'

export function generateAcademicYears(startYear = 2020, endYear = 2035) {
  const years = []
  for (let y = endYear; y >= startYear; y--) {
    const nextY = String(y + 1).slice(-2)
    const val = `${y}-${nextY}`
    years.push({ value: val, label: val })
  }
  return years
}

const ALL_ACADEMIC_YEARS = generateAcademicYears()

function parseTimeString(timeStr) {
  if (!timeStr) return { hour: '10', minute: '00', amPm: 'AM' }
  const match = String(timeStr).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i)
  if (match) {
    let h = parseInt(match[1], 10)
    const m = match[2]
    let p = (match[3] || 'AM').toUpperCase()
    if (h > 12) {
      h -= 12
      p = 'PM'
    } else if (h === 0) {
      h = 12
    }
    return { hour: String(h).padStart(2, '0'), minute: m, amPm: p }
  }
  return { hour: '10', minute: '00', amPm: 'AM' }
}

function buildTimeString(hour, minute, amPm) {
  return `${String(hour || '10').padStart(2, '0')}:${String(minute || '00').padStart(2, '0')} ${(amPm || 'AM').toUpperCase()}`
}

const DEFAULT_FORM = {
  academicYear: '2026-27',
  testNo: 0,
  ignoreDuplication: false,
  testDate: new Date().toISOString().slice(0, 10),
  planHour: '10',
  planMinute: '00',
  planAmPm: 'AM',
  tillHour: '11',
  tillMinute: '00',
  tillAmPm: 'AM',
  course: '',
  subject: '',
  topic: '',
  company: '',
  companyScope: 'Single',
  branch: '',
  branchScope: 'Single',
  batch: '',
  batchScope: 'Single',
  schoolCollege: '',
  schoolCollegeScope: 'Single',
  totalMarks: 100,
  minMarks: 35,
  department: '',
  testGroup: '',
  semester: 0,
  testType: 1,
  examType: '',
  examFor: '',
}

export function useTestEntryScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const testId = searchParams.get('id')
  const isEdit = Boolean(testId)

  const [form, setForm] = useState(DEFAULT_FORM)
  const [isLoading, setIsLoading] = useState(isEdit)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  // Live master options from Database (Rule 90)
  const [masterOptions, setMasterOptions] = useState({
    courses: [],
    subjects: [],
    branches: [],
    batches: [],
    companies: [],
    schools: [],
    departments: [],
    groups: [],
  })

  // Load masters on mount
  useEffect(() => {
    let active = true
    Promise.allSettled([
      getMasterOptionItems('courses'),
      getMasterOptionItems('subjects'),
      getMasterOptionItems('branches'),
      getMasterOptionItems('batches'),
      getMasterOptionItems('institutes'),
      getMasterOptionItems('schools'),
      getMasterOptionItems('departments'),
      listTestGroups({ archive: 0 }),
    ]).then(([coursesRes, subjectsRes, branchesRes, batchesRes, instRes, schoolsRes, deptRes, groupsRes]) => {
      if (!active) return
      setMasterOptions({
        courses: coursesRes.status === 'fulfilled' ? coursesRes.value : [],
        subjects: subjectsRes.status === 'fulfilled' ? subjectsRes.value : [],
        branches: branchesRes.status === 'fulfilled' ? branchesRes.value : [],
        batches: batchesRes.status === 'fulfilled' ? batchesRes.value : [],
        companies: instRes.status === 'fulfilled' ? instRes.value : [],
        schools: schoolsRes.status === 'fulfilled' ? schoolsRes.value : [],
        departments: deptRes.status === 'fulfilled' ? deptRes.value : [],
        groups: groupsRes.status === 'fulfilled' && Array.isArray(groupsRes.value) ? groupsRes.value : [],
      })
    })

    return () => {
      active = false
    }
  }, [])

  // Load existing test for Edit
  useEffect(() => {
    if (!testId) {
      setIsLoading(false)
      return
    }

    let active = true
    setIsLoading(true)
    setError('')

    getTestDetail(testId)
      .then((data) => {
        if (!active || !data) return
        const ft = parseTimeString(data.fromTime)
        const tt = parseTimeString(data.tillTime)

        setForm({
          academicYear: data.academicYear || '2026-27',
          testNo: Number(data.testNo) || 0,
          ignoreDuplication: Boolean(data.ignoreDuplication),
          testDate: data.testDate || new Date().toISOString().slice(0, 10),
          planHour: ft.hour,
          planMinute: ft.minute,
          planAmPm: ft.amPm,
          tillHour: tt.hour,
          tillMinute: tt.minute,
          tillAmPm: tt.amPm,
          course: data.courseId ? String(data.courseId) : '',
          subject: data.subjectId ? String(data.subjectId) : '',
          topic: data.topic || '',
          company: data.instituteId ? String(data.instituteId) : '',
          companyScope: data.instituteType === 2 ? 'All' : 'Single',
          branch: data.branchType === 2 ? '' : String(data.branchesCSV || ''),
          branchScope: data.branchType === 2 ? 'All' : 'Single',
          batch: data.batchType === 2 ? '' : String(data.batchesCSV || ''),
          batchScope: data.batchType === 2 ? 'All' : 'Single',
          schoolCollege: data.schoolType === 2 ? '' : (data.schoolId ? String(data.schoolId) : ''),
          schoolCollegeScope: data.schoolType === 2 ? 'All' : 'Single',
          totalMarks: Number(data.maxMarks) || 100,
          minMarks: Number(data.minMarks) || 35,
          department: data.departmentId ? String(data.departmentId) : '',
          testGroup: data.groupId ? String(data.groupId) : '',
          semester: Number(data.semesterId) || 0,
          testType: Number(data.testType) || 1,
          examType: data.examType || '',
          examFor: data.examFor || '',
        })
      })
      .catch((err) => {
        if (!active) return
        const msg = err?.response?.data?.detail || err?.message || 'Failed to load test details'
        setError(msg)
        appToast.error(msg)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [testId])

  const patchForm = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  const validateForm = () => {
    if (!form.academicYear?.toString().trim()) {
      const msg = 'Academic Year is required'
      setError(msg)
      appToast.error(msg)
      return false
    }
    if (!form.testDate) {
      const msg = 'Test Date is required'
      setError(msg)
      appToast.error(msg)
      return false
    }
    if (!form.course || form.course === '0') {
      const msg = 'Please select a Course'
      setError(msg)
      appToast.error(msg)
      return false
    }
    if (!form.subject || form.subject === '0') {
      const msg = 'Please select a Subject'
      setError(msg)
      appToast.error(msg)
      return false
    }
    const maxM = Number(form.totalMarks || 0)
    if (maxM <= 0) {
      const msg = 'Total / Max Marks must be greater than 0'
      setError(msg)
      appToast.error(msg)
      return false
    }
    const minM = Number(form.minMarks || 0)
    if (minM > maxM) {
      const msg = 'Min / Passing Marks cannot exceed Total Marks'
      setError(msg)
      appToast.error(msg)
      return false
    }
    return true
  }

  const handleSave = async (saveAndContinue = false) => {
    setError('')
    if (!validateForm()) return

    setIsSaving(true)
    try {
      const payload = {
        academicYear: String(form.academicYear || '').trim(),
        testNo: Number(form.testNo) || 0,
        testDate: form.testDate,
        fromTime: buildTimeString(form.planHour, form.planMinute, form.planAmPm),
        tillTime: buildTimeString(form.tillHour, form.tillMinute, form.tillAmPm),
        instituteId: Number(form.company) || 0,
        instituteType: form.companyScope === 'All' ? 2 : 1,
        courseId: Number(form.course) || 0,
        branchType: form.branchScope === 'All' ? 2 : 1,
        branchesCSV: form.branchScope === 'All' ? '0' : String(form.branch || '0'),
        batchType: form.batchScope === 'All' ? 2 : 1,
        batchesCSV: form.batchScope === 'All' ? '0' : String(form.batch || '0'),
        schoolType: form.schoolCollegeScope === 'All' ? 2 : 1,
        schoolId: form.schoolCollegeScope === 'All' ? 0 : (Number(form.schoolCollege) || 0),
        subjectId: Number(form.subject) || 0,
        topic: String(form.topic || '').trim(),
        minMarks: Number(form.minMarks) || 0,
        maxMarks: Number(form.totalMarks) || 100,
        groupId: Number(form.testGroup) || 0,
        testType: Number(form.testType) || 1,
        semesterId: Number(form.semester) || 0,
        departmentId: Number(form.department) || 0,
        examType: String(form.examType || '').trim(),
        examFor: String(form.examFor || '').trim(),
        ignoreDuplication: Boolean(form.ignoreDuplication),
      }

      if (isEdit) {
        await updateTest(testId, payload)
        appToast.success('Test updated successfully')
        navigate('/education/tests')
      } else {
        await createTest(payload)
        appToast.success('Test created successfully')
        if (saveAndContinue) {
          setForm((prev) => ({
            ...prev,
            testNo: 0,
            topic: '',
          }))
        } else {
          navigate('/education/tests')
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save test'
      setError(msg)
      appToast.error(msg)
    } finally {
      setIsSaving(false)
    }
  }

  // Prepared option lists for components
  const toOptions = (list = []) =>
    (list || []).map((it) => ({
      value: it.id ?? it.value,
      label: it.name ?? it.label ?? String(it.id ?? it.value),
    }))

  const options = useMemo(() => ({
    academicYears: ALL_ACADEMIC_YEARS,
    courses: toOptions(masterOptions.courses),
    subjects: toOptions(masterOptions.subjects),
    branches: toOptions(masterOptions.branches),
    batches: toOptions(masterOptions.batches),
    companies: toOptions(masterOptions.companies),
    schools: toOptions(masterOptions.schools),
    departments: toOptions(masterOptions.departments),
    groups: toOptions(masterOptions.groups),
  }), [masterOptions])

  return {
    state: {
      form,
      isEdit,
      testId,
      isLoading,
      isSaving,
      error,
      options,
    },
    actions: {
      patchForm,
      handleSave,
      handleCancel: () => navigate('/education/tests'),
    },
  }
}

export default useTestEntryScreen
